// WebCrypto-Helfer: ECDSA-P256-Schluesselpaar (nicht exportierbarer privater Schluessel,
// Architektur §3), Signieren, Base64 <-> ArrayBuffer.
//
// Formate sind bewusst die "rohen" WebCrypto-Exportformate, keine DER-Huelle -- muss zum
// Rust-Backend passen (p256::ecdsa::VerifyingKey::from_sec1_bytes / Signature::from_slice,
// siehe services/device_auth.rs).

export function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

export function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

/** Erzeugt ein neues Geraete-Schluesselpaar. Der private Schluessel ist `extractable: false`
 * -- er verlaesst das Geraet nie, auch nicht via `exportKey`. Persistiert wird das
 * `CryptoKey`-Objekt selbst (strukturiert klonbar) in IndexedDB, siehe lib/db.ts. */
export async function generateDeviceKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false, // extractable = false
    ['sign', 'verify'],
  ) as Promise<CryptoKeyPair>
}

/** Oeffentlicher Schluessel als roher, unkomprimierter SEC1-Punkt (65 Byte), Base64-kodiert
 * -- das einzige, was das Geraet je preisgibt. */
export async function exportPublicKeyRaw(publicKey: CryptoKey): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', publicKey)
  return bufferToBase64(raw)
}

/** Signiert die Nachricht mit dem privaten Schluessel. Ergebnis ist das rohe (r||s)-Format
 * (64 Byte fuer P-256), Base64-kodiert -- IEEE-P1363-Format, keine DER-Huelle. */
export async function signMessage(privateKey: CryptoKey, message: string): Promise<string> {
  const data = new TextEncoder().encode(message)
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    data,
  )
  return bufferToBase64(signature)
}
